import { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { ArrowLeft, Download, Users, QrCode, Calendar, Store, Trophy, AlertTriangle, Smartphone, StopCircle, Plus, X, Settings, Pencil, Check, ChevronLeft, ChevronRight, MapPin, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import Logo from '../../components/Logo';

interface LayoutContext {
  viewAsStoreId?: string;
  viewAsStoreName?: string;
}

interface QRCodeData {
  id: string;
  code: string;
  url: string;
  qrImagePng: string;
  bannerText: string | null;
  bannerTextColor: string | null;
  showStoreLogo: boolean;
  store: {
    id: string;
    name: string;
    logoUrl: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
  };
  _count: {
    scans: number;
  };
}

interface RunData {
  id: string;
  instanceNumber: number;
  status: string;
  startDate: string;
  endDate: string;
  numberOfWinners: number | null;
  campaign: {
    id: string;
    name: string;
    description: string | null;
    headerColor: string | null;
    numberOfWinners: number;
  };
  _count: {
    scans: number;
    qrCodes: number;
  };
  participations: Array<{
    store: {
      id: string;
      name: string;
    };
  }>;
}

interface RunStats {
  totalScans: number;
  uniqueCustomers: number;
  storeBreakdown: Array<{
    storeId: string;
    storeName: string;
    scans: number;
  }>;
}

interface Winner {
  id: string;
  prizeRank: number;
  customerName: string;
  customerEmail: string;
  totalEntries: number;
  selectedAt: string;
}

interface RaffleStats {
  totalEntries: number;
  uniqueParticipants: number;
  averageEntriesPerPerson: number;
  maxEntriesFromOne: number;
}

export default function CampaignRunViewPage() {
  const { campaignId, runId } = useParams<{ campaignId: string; runId: string }>();
  const { viewAsStoreId, viewAsStoreName } = useOutletContext<LayoutContext>() || {};
  const navigate = useNavigate();

  const [run, setRun] = useState<RunData | null>(null);
  const [qrCode, setQrCode] = useState<QRCodeData | null>(null);
  const [stats, setStats] = useState<RunStats | null>(null);
  const [winners, setWinners] = useState<Winner[]>([]);
  const [raffleStats, setRaffleStats] = useState<RaffleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEndEarlyModal, setShowEndEarlyModal] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [ending, setEnding] = useState(false);
  const [allStores, setAllStores] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [addingStore, setAddingStore] = useState<string | null>(null);
  const [removingStore, setRemovingStore] = useState<string | null>(null);
  const [editingStoreEmail, setEditingStoreEmail] = useState<string | null>(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [phonePreviewView, setPhonePreviewView] = useState<'form' | 'success'>('form');

  const isStoreView = !!viewAsStoreId;

  useEffect(() => {
    loadData();
  }, [runId, viewAsStoreId]);

  const loadData = async () => {
    try {
      // Load run data
      const runResponse = await apiClient.get<{ success: boolean; data: RunData }>(
        `/admin/campaigns/runs/${runId}`
      );
      setRun(runResponse.data.data);

      // If in store view, load the QR code for this store
      if (viewAsStoreId) {
        try {
          const qrResponse = await apiClient.get<{ success: boolean; data: QRCodeData[] }>(
            `/admin/stores/${viewAsStoreId}/qrcodes`
          );
          // Find QR code for this run
          const myQrCode = qrResponse.data.data.find((qr: any) => qr.run?.id === runId);
          if (myQrCode) {
            // Get full QR code data
            const fullQrResponse = await apiClient.get<{ success: boolean; data: QRCodeData }>(
              `/admin/stores/${viewAsStoreId}/qrcodes/${myQrCode.id}`
            );
            setQrCode(fullQrResponse.data.data);
          }
        } catch (e) {
          console.error('Failed to load QR code:', e);
        }
      }

      // Load stats
      try {
        const statsResponse = await apiClient.get<{ success: boolean; data: any }>(
          `/admin/campaigns/runs/${runId}/stats`
        );
        const statsData = statsResponse.data.data;
        setStats({
          totalScans: statsData.run?._count?.scans || 0,
          uniqueCustomers: statsData.uniqueCustomers || 0,
          storeBreakdown: statsData.scansByStore || [],
        });
      } catch (e) {
        console.error('Failed to load stats:', e);
      }

      // Load raffle stats (for admin view)
      if (!viewAsStoreId) {
        try {
          const raffleResponse = await apiClient.get<{ success: boolean; data: RaffleStats }>(
            `/admin/campaigns/runs/${runId}/raffle-stats`
          );
          setRaffleStats(raffleResponse.data.data);
        } catch (e) {
          console.error('Failed to load raffle stats:', e);
        }

        // Load winners if any
        try {
          const winnersResponse = await apiClient.get<{ success: boolean; data: Winner[] }>(
            `/admin/campaigns/runs/${runId}/winners`
          );
          setWinners(winnersResponse.data.data);
        } catch (e) {
          console.error('Failed to load winners:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load run data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!qrCode || !viewAsStoreId) return;

    try {
      const blob = await apiClient.getBlob(`/admin/stores/${viewAsStoreId}/qrcodes/${qrCode.id}/pdf`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${viewAsStoreName}_${run?.campaign.name}_Run${run?.instanceNumber}_QR.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  const goBack = () => {
    navigate('/admin/campaigns');
  };

  const handleEndCampaignEarly = async () => {
    setEnding(true);
    try {
      // Change status to COMPLETED
      await apiClient.put(`/admin/campaigns/runs/${runId}/status`, { status: 'COMPLETED' });

      setShowEndEarlyModal(false);
      // Reload to get updated status
      loadData();
    } catch (error: any) {
      console.error('Failed to end campaign early:', error);
      alert(error.message || 'Failed to end campaign early');
    } finally {
      setEnding(false);
    }
  };

  const loadAllStores = async () => {
    setLoadingStores(true);
    try {
      const response = await apiClient.get<{ success: boolean; data: Array<{ id: string; name: string; email: string }> }>(
        '/admin/stores'
      );
      setAllStores(response.data.data);
    } catch (error) {
      console.error('Failed to load stores:', error);
    } finally {
      setLoadingStores(false);
    }
  };

  const handleOpenStoreModal = () => {
    loadAllStores();
    setShowStoreModal(true);
  };

  const handleAddStore = async (storeId: string) => {
    setAddingStore(storeId);
    try {
      await apiClient.post(`/admin/campaigns/runs/${runId}/stores`, { storeIds: [storeId] });
      // Reload run data to get updated participations
      loadData();
    } catch (error: any) {
      console.error('Failed to add store:', error);
      alert(error.message || 'Failed to add store');
    } finally {
      setAddingStore(null);
    }
  };

  const handleRemoveStore = async (storeId: string) => {
    if (!confirm('Remove this store from the campaign? Their QR code will be deleted.')) return;

    setRemovingStore(storeId);
    try {
      await apiClient.delete(`/admin/campaigns/runs/${runId}/stores/${storeId}`);
      // Reload run data to get updated participations
      loadData();
    } catch (error: any) {
      console.error('Failed to remove store:', error);
      alert(error.message || 'Failed to remove store');
    } finally {
      setRemovingStore(null);
    }
  };

  const handleStartEditEmail = (storeId: string, currentEmail: string) => {
    setEditingStoreEmail(storeId);
    setEditEmailValue(currentEmail);
  };

  const handleCancelEditEmail = () => {
    setEditingStoreEmail(null);
    setEditEmailValue('');
  };

  const handleSaveEmail = async (storeId: string) => {
    if (!editEmailValue.trim()) {
      alert('Email cannot be empty');
      return;
    }

    setSavingEmail(true);
    try {
      await apiClient.put(`/admin/stores/${storeId}`, { email: editEmailValue.trim() });
      // Reload stores and run data
      loadAllStores();
      loadData();
      setEditingStoreEmail(null);
      setEditEmailValue('');
    } catch (error: any) {
      console.error('Failed to update email:', error);
      alert(error.message || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const goToDesigner = () => {
    if (qrCode && viewAsStoreId) {
      navigate(`/admin/qrcodes/${qrCode.id}?viewAs=${viewAsStoreId}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      ACTIVE: 'bg-green-100 text-green-800',
      DRAFT: 'bg-gray-100 text-gray-800',
      PAUSED: 'bg-yellow-100 text-yellow-800',
      COMPLETED: 'bg-blue-100 text-blue-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (!run) {
    return <div className="text-red-600">Campaign run not found</div>;
  }

  const headerColor = run.campaign.headerColor || '#0891b2';
  const primaryColor = qrCode?.store?.primaryColor || '#000000';
  const secondaryColor = qrCode?.store?.secondaryColor || '#FFFFFF';

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button onClick={goBack} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{run.campaign.name}</h1>
            <p className="text-sm text-gray-500">Occurrence #{run.instanceNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(run.status)}`}>
            {run.status}
          </span>
          {isStoreView && qrCode && (
            <>
              <button
                onClick={goToDesigner}
                className="btn-secondary"
              >
                Edit Design
              </button>
              <button
                onClick={handleDownloadPDF}
                className="btn-primary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </>
          )}
          {!isStoreView && run.status === 'ACTIVE' && (
            <button
              onClick={() => setShowEndEarlyModal(true)}
              className="btn-secondary flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <StopCircle className="w-4 h-4" />
              End Campaign Early
            </button>
          )}
          {!isStoreView && run?.status === 'COMPLETED' && winners.length === 0 && raffleStats && raffleStats.uniqueParticipants > 0 && (
            <button
              onClick={() => navigate('/admin/winners')}
              className="btn-primary flex items-center gap-2"
            >
              <Trophy className="w-4 h-4" />
              Go to Winners Dashboard
            </button>
          )}
        </div>
      </div>

      {/* Two Previews Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Left Column - QR Flyer Preview */}
        {isStoreView && qrCode ? (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Your QR Code Flyer</h2>
            <p className="text-sm text-gray-500 mb-4">
              This is how your QR code flyer will look when printed.
            </p>

            {/* Flyer Preview */}
            <div
              className="bg-white border-2 border-gray-200 rounded-lg shadow-lg mx-auto flex flex-col"
              style={{ width: '100%', maxWidth: '350px', aspectRatio: '8.5 / 11' }}
            >
              {/* Header */}
              <div
                className="py-3 px-4 flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: headerColor }}
              >
                <div className="flex items-center gap-2 text-white">
                  <Logo size="sm" />
                  <h3 className="font-bold text-base">Discover The Park</h3>
                </div>
              </div>

              {/* Campaign Name */}
              <div className="text-center py-3 px-4 flex-shrink-0">
                <h2 className="text-lg font-bold text-gray-900">{run.campaign.name}</h2>
                {run.campaign.description && (
                  <p className="text-gray-600 mt-1 text-xs">{run.campaign.description}</p>
                )}
                <p className="text-gray-500 mt-1 text-xs">Winners will be notified by email</p>
              </div>

              {/* QR Code */}
              <div className="flex-1 flex flex-col items-center justify-center py-2">
                <div className="bg-white p-2 rounded-lg shadow-inner border border-gray-100">
                  {qrCode.qrImagePng ? (
                    <img
                      src={qrCode.qrImagePng}
                      alt="QR Code"
                      className="w-28 h-28"
                    />
                  ) : (
                    <div className="w-28 h-28 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                      QR Code
                    </div>
                  )}
                </div>
                <p className="text-center text-gray-600 font-medium text-sm mt-2">
                  Scan to Enter!
                </p>
              </div>

              {/* Store Banner */}
              <div
                className="py-3 px-4 flex items-center justify-between flex-shrink-0"
                style={{ backgroundColor: secondaryColor }}
              >
                <div className="flex-1">
                  <p className="font-semibold text-sm" style={{ color: qrCode.bannerTextColor || primaryColor }}>
                    {qrCode.bannerText || qrCode.store.name}
                  </p>
                </div>
                {qrCode.showStoreLogo && qrCode.store.logoUrl && (
                  <img
                    src={qrCode.store.logoUrl}
                    alt={qrCode.store.name}
                    className="w-8 h-8 object-contain"
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">QR Code Flyer Preview</h2>
            <p className="text-sm text-gray-500 mb-4">
              This is how the QR code flyer will look for each participating store.
            </p>

            {/* Sample Flyer Preview */}
            <div
              className="bg-white border-2 border-gray-200 rounded-lg shadow-lg mx-auto flex flex-col"
              style={{ width: '100%', maxWidth: '350px', aspectRatio: '8.5 / 11' }}
            >
              {/* Header */}
              <div
                className="py-3 px-4 flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: headerColor }}
              >
                <div className="flex items-center gap-2 text-white">
                  <Logo size="sm" />
                  <h3 className="font-bold text-base">Discover The Park</h3>
                </div>
              </div>

              {/* Campaign Name */}
              <div className="text-center py-3 px-4 flex-shrink-0">
                <h2 className="text-lg font-bold text-gray-900">{run.campaign.name}</h2>
                {run.campaign.description && (
                  <p className="text-gray-600 mt-1 text-xs">{run.campaign.description}</p>
                )}
                <p className="text-gray-500 mt-1 text-xs">Winners will be notified by email</p>
              </div>

              {/* Sample QR Code */}
              <div className="flex-1 flex flex-col items-center justify-center py-2">
                <div className="relative bg-white p-2 rounded-lg shadow-inner border border-gray-100">
                  {/* Sample QR pattern */}
                  <div className="w-28 h-28 bg-gray-100 flex items-center justify-center">
                    <QrCode className="w-20 h-20 text-gray-300" />
                  </div>
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <div className="text-center px-2">
                      <p className="text-white text-xs font-semibold">SAMPLE</p>
                      <p className="text-white/80 text-[10px] mt-1">Each store gets a unique code</p>
                    </div>
                  </div>
                </div>
                <p className="text-center text-gray-600 font-medium text-sm mt-2">
                  Scan to Enter!
                </p>
              </div>

              {/* Placeholder Store Banner */}
              <div className="py-3 px-4 flex items-center justify-between flex-shrink-0 bg-gray-100 border-t-2 border-dashed border-gray-300">
                <div className="flex-1">
                  <p className="font-semibold text-sm text-gray-400 italic">[Store Name]</p>
                  <p className="text-[10px] text-gray-400">Store branding & colors appear here</p>
                </div>
                <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                  <Store className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Right Column - Customer Scan Experience Preview */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Smartphone className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Customer Scan Experience</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            This is what customers see on their phone when they scan the QR code.
          </p>

          {/* Toggle Buttons */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <button
              onClick={() => setPhonePreviewView('form')}
              disabled={phonePreviewView === 'form'}
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-gray-700 min-w-[120px] text-center">
              {phonePreviewView === 'form' ? 'Registration Form' : 'Success Page'}
            </span>
            <button
              onClick={() => setPhonePreviewView('success')}
              disabled={phonePreviewView === 'success'}
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Phone Mockup */}
          <div className="mx-auto" style={{ maxWidth: '280px' }}>
            {/* Phone Frame */}
            <div className="relative bg-gray-900 rounded-[2.5rem] p-2 shadow-xl">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10"></div>

              {/* Screen */}
              <div className="bg-white rounded-[2rem] overflow-hidden" style={{ aspectRatio: '9 / 19' }}>
                {phonePreviewView === 'form' ? (
                  <>
                    {/* Status Bar */}
                    <div className="h-8 bg-gray-100 flex items-center justify-between px-6 text-[10px] text-gray-600">
                      <span>9:41</span>
                      <div className="flex items-center gap-1">
                        <span>100%</span>
                      </div>
                    </div>

                    {/* Store Header */}
                    <div
                      className="py-4 px-4 text-center"
                      style={{ backgroundColor: isStoreView && qrCode ? primaryColor : '#22c55e' }}
                    >
                      {isStoreView && qrCode?.store.logoUrl && (
                        <div className="w-10 h-10 mx-auto mb-2 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                          <img src={qrCode.store.logoUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      {!isStoreView && (
                        <div className="w-10 h-10 mx-auto mb-2 bg-white/20 rounded-lg flex items-center justify-center">
                          <Store className="w-5 h-5 text-white/80" />
                        </div>
                      )}
                      <p className="text-white font-bold text-sm">
                        {isStoreView && qrCode ? qrCode.store.name : '[Store Name]'}
                      </p>
                    </div>

                    {/* Campaign Info */}
                    <div className="py-3 px-4 text-center">
                      <h3 className="font-bold text-gray-900 text-sm">{run.campaign.name}</h3>
                      {run.campaign.description && (
                        <p className="text-gray-600 text-[10px] mt-1">{run.campaign.description}</p>
                      )}
                    </div>

                    {/* Registration Form */}
                    <div className="px-4 pb-4">
                      <div className="bg-white rounded-lg shadow-md border border-gray-100 p-3">
                        <h4 className="font-bold text-gray-900 text-xs mb-3 text-center">
                          Enter the Raffle!
                        </h4>

                        {/* Form Fields */}
                        <div className="space-y-2">
                          <div>
                            <label className="block text-[10px] text-gray-600 mb-1">Your Name</label>
                            <div className="bg-gray-100 rounded px-2 py-1.5 text-[10px] text-gray-400">
                              John Doe
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-600 mb-1">Email Address</label>
                            <div className="bg-gray-100 rounded px-2 py-1.5 text-[10px] text-gray-400">
                              john@example.com
                            </div>
                          </div>
                          <button
                            className="w-full py-2 rounded text-white text-[10px] font-bold"
                            style={{ backgroundColor: isStoreView && qrCode ? primaryColor : '#22c55e' }}
                          >
                            Enter Raffle
                          </button>
                        </div>

                        <p className="text-[8px] text-gray-400 text-center mt-2">
                          Each scan = 1 raffle entry
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Success View */
                  <div
                    className="h-full flex flex-col items-center justify-center p-4 pt-10"
                    style={{ backgroundColor: isStoreView && qrCode ? primaryColor : '#22c55e' }}
                  >
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <Logo size="sm" className="drop-shadow-lg" />
                      <div className="text-white">
                        <h2 className="text-xs font-bold leading-tight">Discover</h2>
                        <h2 className="text-xs font-bold leading-tight">the Park</h2>
                      </div>
                    </div>

                    {/* Campaign Badge */}
                    <div className="bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 mb-3">
                      <span className="text-white text-[10px] font-medium">{run.campaign.name}</span>
                    </div>

                    <div className="text-center text-white">
                      <div className="text-3xl mb-2">🎉</div>
                      <h1 className="text-lg font-bold mb-1">You're In!</h1>
                      <p className="text-xs mb-1">
                        You now have <span className="font-bold text-xl">3</span> entries!
                      </p>
                      <p className="text-white/90 text-[10px] mb-3">
                        You will be contacted by email if you won a prize on or about {format(new Date(run.endDate), 'MMMM d, yyyy')}.
                      </p>

                      {/* Mini Map Preview */}
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <p className="text-white/90 text-[10px] mb-2">
                          Visit other stores for more chances!
                        </p>
                        <div className="bg-white rounded-lg p-1.5 shadow-lg">
                          <div className="bg-gray-200 rounded h-16 flex items-center justify-center">
                            <div className="text-center">
                              <MapPin className="w-4 h-4 text-primary-600 mx-auto" />
                              <p className="text-[8px] text-gray-600">View Map</p>
                            </div>
                          </div>
                        </div>
                        <button className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-white text-gray-900 rounded text-[10px] font-semibold">
                          <MapPin className="w-2.5 h-2.5" />
                          View Other Stores
                          <ExternalLink className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Home Indicator */}
            <div className="flex justify-center mt-1">
              <div className="w-20 h-1 bg-gray-300 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Occurrence Details (for admin view) */}
      {!isStoreView && (
        <div className="card mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Occurrence Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-medium">
                  {format(new Date(run.startDate), 'MMM d, yyyy')} - {format(new Date(run.endDate), 'MMM d, yyyy')}
                </p>
              </div>
            </div>

            <button
              onClick={handleOpenStoreModal}
              className="flex items-center gap-3 hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors text-left"
            >
              <Store className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Participating Stores</p>
                <p className="font-medium">{run.participations.length} stores</p>
              </div>
              <Settings className="w-4 h-4 text-gray-400 ml-2" />
            </button>

            <div className="flex items-center gap-3">
              <QrCode className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">QR Codes</p>
                <p className="font-medium">{run._count.qrCodes} generated</p>
              </div>
            </div>
          </div>

          {/* Store list */}
          {run.participations.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Enrolled Stores</h3>
                <button
                  onClick={handleOpenStoreModal}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Manage
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {run.participations.map((p) => (
                  <span key={p.store.id} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600">
                    {p.store.name}
                    <button
                      onClick={() => handleRemoveStore(p.store.id)}
                      disabled={removingStore === p.store.id}
                      className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove store"
                    >
                      {removingStore === p.store.id ? (
                        <span className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin inline-block"></span>
                      ) : (
                        <X className="w-3 h-3" />
                      )}
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Stats Cards + Raffle Info */}
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {isStoreView && qrCode ? qrCode._count?.scans || 0 : stats?.totalScans || 0}
                  </p>
                  <p className="text-sm text-gray-500">Total Scans</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.uniqueCustomers || 0}</p>
                  <p className="text-sm text-gray-500">Unique Entries</p>
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Info / Raffle Stats */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {winners.length > 0 ? 'Raffle Results' : 'Raffle Info'}
            </h2>
            <div className="space-y-3">
              {run.campaign.description && (
                <p className="text-gray-600 mb-4">{run.campaign.description}</p>
              )}

              {/* Show raffle statistics */}
              {raffleStats && raffleStats.uniqueParticipants > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-gray-700">
                    <span className="font-semibold text-gray-900">{raffleStats.uniqueParticipants}</span> customer{raffleStats.uniqueParticipants !== 1 ? 's' : ''} with{' '}
                    <span className="font-semibold text-gray-900">{raffleStats.totalEntries}</span> total entr{raffleStats.totalEntries !== 1 ? 'ies' : 'y'}.{' '}
                    <span className="font-semibold text-gray-900">{run.numberOfWinners || run.campaign.numberOfWinners}</span> winner{(run.numberOfWinners || run.campaign.numberOfWinners) !== 1 ? 's' : ''} will be drawn.
                  </p>
                  <p className="text-sm text-gray-600">
                    More scans = more entries = better odds of winning.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Avg {raffleStats.averageEntriesPerPerson} entries per person
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 text-gray-500 text-center">
                  No entries yet. Customers need to scan QR codes to enter.
                </div>
              )}

              <div className="pt-3 border-t space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Winners to be drawn</span>
                  <span className="font-medium">{run.numberOfWinners || run.campaign.numberOfWinners}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Campaign ends</span>
                  <span className="font-medium">{format(new Date(run.endDate), 'MMMM d, yyyy')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* QR Code URL (for store view) */}
          {isStoreView && qrCode && (
            <div className="card bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">QR Code URL</h3>
              <p className="text-xs text-gray-500 mb-2">
                Customers can also visit this link directly:
              </p>
              <code className="text-xs bg-white px-3 py-2 rounded border block break-all">
                {qrCode.url}
              </code>
            </div>
          )}
        </div>

        {/* Winners Section */}
        {winners.length > 0 && (
          <div className="card h-fit">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Winners
            </h2>
            <div className="space-y-3">
              {winners.map((winner) => (
                <div key={winner.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-400 text-yellow-900 font-bold flex items-center justify-center text-sm">
                      #{winner.prizeRank}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{winner.customerName}</p>
                      <p className="text-sm text-gray-500">{winner.customerEmail}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700">{winner.totalEntries} entries</p>
                    <p className="text-xs text-gray-500">
                      {format(new Date(winner.selectedAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* End Campaign Early Modal */}
      {showEndEarlyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <StopCircle className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">End Campaign Early</h2>
            </div>

            <p className="text-gray-600 mb-4">
              Are you sure you want to end <span className="font-semibold">"{run?.campaign.name} (Run #{run?.instanceNumber})"</span> before its scheduled end date of{' '}
              <span className="font-semibold">{run && format(new Date(run.endDate), 'MMMM d, yyyy')}</span>?
            </p>

            {raffleStats && raffleStats.uniqueParticipants > 0 ? (
              <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
                <p className="text-gray-700">
                  <span className="font-semibold">{raffleStats.uniqueParticipants}</span> customers have entered with{' '}
                  <span className="font-semibold">{raffleStats.totalEntries}</span> total entries.
                </p>
                <p className="text-sm text-gray-600">
                  After ending, you can draw winners from the Winners Dashboard.
                </p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 text-sm">
                  No entries have been recorded yet. You can still end the campaign, but there will be no winners to draw.
                </p>
              </div>
            )}

            <div className="flex items-start gap-2 mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                QR codes will stop accepting new scans. Customers who try to scan will see a message that the campaign has ended.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleEndCampaignEarly}
                disabled={ending}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {ending ? (
                  <>Processing...</>
                ) : (
                  <>
                    <StopCircle className="w-4 h-4" />
                    End Campaign
                  </>
                )}
              </button>
              <button
                onClick={() => setShowEndEarlyModal(false)}
                disabled={ending}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Stores Modal */}
      {showStoreModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Manage Participating Stores</h2>
              <button
                onClick={() => setShowStoreModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Add or remove stores from this occurrence. Adding a store will automatically generate their QR code.
            </p>

            {loadingStores ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2">
                {/* Currently participating stores */}
                {run?.participations && run.participations.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Currently Enrolled ({run.participations.length})</h3>
                    <div className="space-y-2">
                      {run.participations.map((p) => {
                        const storeEmail = allStores.find(s => s.id === p.store.id)?.email || '';
                        const isEditing = editingStoreEmail === p.store.id;

                        return (
                          <div
                            key={p.store.id}
                            className="p-3 bg-green-50 border border-green-200 rounded-lg"
                          >
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-gray-900">{p.store.name}</p>
                              <button
                                onClick={() => handleRemoveStore(p.store.id)}
                                disabled={removingStore === p.store.id}
                                className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1"
                              >
                                {removingStore === p.store.id ? (
                                  <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></span>
                                ) : (
                                  <>
                                    <X className="w-4 h-4" />
                                    Remove
                                  </>
                                )}
                              </button>
                            </div>
                            {/* Email row with edit */}
                            <div className="mt-2">
                              {isEditing ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="email"
                                    value={editEmailValue}
                                    onChange={(e) => setEditEmailValue(e.target.value)}
                                    className="flex-1 text-sm px-2 py-1 border rounded"
                                    placeholder="email@example.com"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleSaveEmail(p.store.id)}
                                    disabled={savingEmail}
                                    className="text-green-600 hover:text-green-700 p-1"
                                    title="Save"
                                  >
                                    {savingEmail ? (
                                      <span className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin inline-block"></span>
                                    ) : (
                                      <Check className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={handleCancelEditEmail}
                                    disabled={savingEmail}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <p className="text-sm text-gray-600">{storeEmail || 'No email'}</p>
                                  <button
                                    onClick={() => handleStartEditEmail(p.store.id, storeEmail)}
                                    className="text-gray-400 hover:text-primary-600 p-1"
                                    title="Edit email"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Available stores to add */}
                {(() => {
                  const participatingIds = new Set(run?.participations.map((p) => p.store.id) || []);
                  const availableStores = allStores.filter((s) => !participatingIds.has(s.id));

                  if (availableStores.length === 0) {
                    return (
                      <div className="text-center py-4 text-gray-500">
                        All stores are already enrolled in this occurrence.
                      </div>
                    );
                  }

                  return (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Available Stores ({availableStores.length})</h3>
                      <div className="space-y-2">
                        {availableStores.map((store) => (
                          <div
                            key={store.id}
                            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{store.name}</p>
                              <p className="text-sm text-gray-500">{store.email}</p>
                            </div>
                            <button
                              onClick={() => handleAddStore(store.id)}
                              disabled={addingStore === store.id}
                              className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1"
                            >
                              {addingStore === store.id ? (
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              ) : (
                                <>
                                  <Plus className="w-4 h-4" />
                                  Add
                                </>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="mt-4 pt-4 border-t">
              <button
                onClick={() => setShowStoreModal(false)}
                className="btn-secondary w-full"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
